import React, { useState, useMemo, useRef } from "react";
import { Col, Row } from "react-bootstrap";

import { TotalSalesForm } from "./TotalSalesForm";
import ErrorHandler from "./../../core/ErrorHandler";
import { DetailsModal } from "../../grid/DetailsModal";
import { LoadingSpinner } from "./../../layout/LoadingSpinner";
import Accordion from "../../Accordion";
import { GridColumn } from "@progress/kendo-react-grid";
import { ZIndexContext } from "@progress/kendo-react-common";
import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";

import {
  DefaultColumnWidth,
  DefaultDateColumnWidth,
  DefaultBooleanOrShortWidthColumnWidth,
} from "../../grid/Kendo/KendoGridAndColumnConstants";

import {
  MoneyCell,
  AggregateMoneyFooterCell,
  AggregateNumberFooterCell,
  TooltipCell,
} from "../../grid/Kendo/CustomKendoGridCells";
import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";
import { ConstructAggregateSetting } from "./helper/ConstructAggregateSetting";
import { useInsertServerSideExport } from "shared/hooks/useInsertServerSideExport";
import { GetISODate, DateWithoutTimeAsFormattedString } from "shared/helpers/DateHelpers";

export function TotalSalesByBin(props) {
  const { passedProps } = props;
  const user = passedProps.user.email;
  const [error, setError] = useState({});

  const [submittedFormData, setSubmittedFormData] = useState({});

  const [mainGridColumns, setMainGridColumns] = useState([]);
  const [mainGridAggregates, setMainGridAggregates] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [detailError, setDetailError] = useState({});
  let detailGridAggregateColumnSettings = [
    { aggregate: "sum", field: "AdjudicatedAmount" },
    { aggregate: "sum", field: "PaidAmount" },
    { aggregate: "sum", field: "CoPay" },
  ];
  const [detailGridAggregates, setDetailGridAggregates] = useState([]);

  const _container = React.useRef();
  const clientGridRef = useRef();
  const clientDetailGridRef = useRef();
  const sendMainAggregatesToParent = (aggregatesFromSharedTotalSalesGrid) => {
    setMainGridAggregates(aggregatesFromSharedTotalSalesGrid.data);
  };

  const sendDetailAggregatesToParent = (aggregatesFromSharedTotalSalesGrid) => {
    setDetailGridAggregates(aggregatesFromSharedTotalSalesGrid.data);
  };

  const [detailsGridApiEndpoint, setDetailsGridApiEndpoint] = useState("");
  const [detailsGridParameters, setDetailsGridParameters] = useState({});

  let { mutate: mutateForServerSideExport } = useInsertServerSideExport();

  const {
    gridDataStateResetProcessingInitiated,
    InitiateGridDataStateReset,
    GridDataStateResetCompleted: onGridDataStateResetCompleted,
  } = useGridDataStateResetProcessing();

  function handleFormSubmit(e) {
    setMainGridColumns([]);
    setSubmittedFormData(e);
    InitiateGridDataStateReset();
  }

  const binApiParams = useMemo(() => {
    if (IsNullOrEmpty(user)) {
      return {};
    }

    if (IsNullOrEmpty(submittedFormData.selectedLocation)) {
      return {};
    }

    let params = {
      user: user,
      toDate: DateWithoutTimeAsFormattedString(submittedFormData.toDate),
      fromDate: DateWithoutTimeAsFormattedString(submittedFormData.fromDate),
      dateType: submittedFormData.dateType,
      paidAmountType: submittedFormData.paidAmountType,
      includeCopay: submittedFormData.includeCopay,
      hideIgnored: submittedFormData.omitIgnored,
      mmids: submittedFormData.selectedLocation,
      includeClid2: submittedFormData.includeClid2,
    };

    return params;
  }, [submittedFormData]);



  function CreateGridColumn(column) {
    let columnWidth = DefaultColumnWidth();

    let filterType = "text";
    if (column.type === "money" || column.type === "number") {
      filterType = "numeric";
    }

    if (column.type === "money") {
      return (
        <GridColumn
          key={column.field}
          filter={filterType}
          field={column.field}
          title={column.title}
          width={columnWidth}
          locked={column.locked}
          cells={{
            data: MainGridMoneyCell,
            footerCell: MainGridAggregateMoneyFooterCell,
          }}
          footerCell={MainGridAggregateMoneyFooterCell}
          headerCell={TooltipCell}
        />
      );
    } else if (column.type === "number") {
      return (
        <GridColumn
          key={column.field}
          filter={filterType}
          field={column.field}
          title={column.title}
          width={columnWidth}
          locked={column.locked}
          cells={{
            footerCell: AggregateFooterCell,
          }}
          footerCell={AggregateFooterCell}
          headerCell={TooltipCell}
        />
      );
    } else {
      return (
        <GridColumn
          key={column.field}
          filter={filterType}
          field={column.field}
          title={column.title}
          width={columnWidth}
          locked={column.locked}
          headerCell={TooltipCell}
        />
      );
    }
  }

  function getBinDetails(props) {
    setShowModal(true);
    setDetailError({});
    var parameters1 = {};

    if (props.isAggregate) {
      parameters1 = {
        user: user,
        bin: props.pushBIN.toString(),
        searchStartDate: DateWithoutTimeAsFormattedString(props.startDate),
        searchEndDate: DateWithoutTimeAsFormattedString(props.endDate),
        hideIgnored: submittedFormData.omitIgnored,
        mmids: submittedFormData.selectedLocation,
        DateType: submittedFormData.dateType,
        paidAmountType: submittedFormData.paidAmountType,
        payer: props.pushPayer.toString(),
        payersForUnmatchedPayments: props.pushPayersForNullBins.toString(),
        includeClid2: submittedFormData.includeClid2,
      }
      setDetailsGridApiEndpoint("api/TotalSales/GetByBinAggregateDetails");
      setDetailsGridParameters(parameters1);
    }
    else {
      if (props.field == "TotalSales") {
        parameters1 = {
          bin: props.dataItem["BIN"],
          searchStartDate: DateWithoutTimeAsFormattedString(submittedFormData.fromDate),
          searchEndDate: DateWithoutTimeAsFormattedString(submittedFormData.toDate),
          dateType: submittedFormData.dateType,
          hideIgnored: submittedFormData.omitIgnored,
          mmids: submittedFormData.selectedLocation,
          user: user,
          paidAmountType: submittedFormData.paidAmountType,
          payer: props.dataItem["Pbid"],
          payersForUnmatchedPayments: props.dataItem["BIN"] ? "" : props.dataItem["Pbid"],
          includeClid2: submittedFormData.includeClid2,
        }

        setDetailsGridApiEndpoint("api/TotalSales/GetByBinAggregateDetails");
        setDetailsGridParameters(parameters1);
      }
      else {
        parameters1 = {
          bin: props.dataItem["BIN"],
          searchDate: props.field,
          dateType: submittedFormData.dateType,
          hideIgnored: submittedFormData.omitIgnored,
          mmids: submittedFormData.selectedLocation,
          user: user,
          paidAmountType: submittedFormData.paidAmountType,
          payer: props.dataItem["Pbid"],
          payersForUnmatchedPayments: props.dataItem["BIN"] ? "" : props.dataItem["Pbid"],
          includeClid2: submittedFormData.includeClid2
        }
        setDetailsGridApiEndpoint("api/TotalSales/GetByBinDetails");
        setDetailsGridParameters(parameters1);
      }
    }
  }

  let MainGridMoneyCell = (props) => {
    let onCellClick = () => {
      let inputProps = {
        ...props,
        isAggregate: false
      };
      getBinDetails(inputProps);
    };

    let options = {
      onClick: onCellClick,
    };

    return MoneyCell(props, options);
  };

  let AggregateFooterCell = (props) => {
    var options = {
      aggregateData: mainGridAggregates,
    };
    return AggregateNumberFooterCell(props, options);
  };

  let DetailsGridAggregateMoneyCell = (props) => {
    var options = {
      aggregateData: detailGridAggregates,
    };
    return AggregateMoneyFooterCell(props, options);
  };

  const MainGridAggregateMoneyFooterCell = (props) => {
    let onCellClick = () => {
      var startDate;
      var endDate;
      if (props.field == "TotalSales") {
        startDate = submittedFormData.fromDate;
        endDate = submittedFormData.toDate;
      } else {
        startDate = props.field;
        endDate = props.field;
      }

      const filteredDataFromComponent = clientGridRef.current
        ? clientGridRef.current.GetGridFilteredData()
        : [];

      let allFilteredBins = "";
      let allFilteredPayers = "";
      //NOTE: We need this so that we know which payers to show unmatched payment data
      let allFilteredPayersForNullBins = "";

      if (filteredDataFromComponent) {
        let uniqueBins = [...new Set(filteredDataFromComponent.map(item => item["BIN"]))];
        let uniquePayers = [...new Set(filteredDataFromComponent.map(item => item["Pbid"]))];
        let uniqueFilteredPayersForNullBins = [...new Set(filteredDataFromComponent.filter(item => item["BIN"] === null).map(item => item["Pbid"]))];

        allFilteredBins = uniqueBins.join(",");
        allFilteredPayers = uniquePayers.join(",");
        allFilteredPayersForNullBins = uniqueFilteredPayersForNullBins.join(",");

        // filteredDataFromComponent.forEach((element) => {
        //   allFilteredBins = allFilteredBins + element["BIN"] + ",";
        //   allFilteredPayers = allFilteredPayers + element["Pbid"] + ",";

        //   if(element["BIN"] === null) {
        //     allFilteredPayersForNullBins = allFilteredPayersForNullBins + element["Pbid"] + ",";
        //   }
        // }
      // );
      } else {
        alert("No data is displayed on grid");
        return null;
      }

      getBinDetails({
        isAggregate: true,
        pushBIN: allFilteredBins,
        pushPayer: allFilteredPayers,
        pushPayersForNullBins: allFilteredPayersForNullBins,
        startDate: DateWithoutTimeAsFormattedString(startDate),
        endDate: DateWithoutTimeAsFormattedString(endDate)        
      })
    };

    var options = {
      aggregateData: mainGridAggregates,
      onClick: onCellClick,
    };
    return AggregateMoneyFooterCell(props, options);
  };

  function detailsGridErrorHandlerCallback(error) {
    let exportRequestParameters = { ...detailsGridParameters };

    if (exportRequestParameters.searchDate) {
      exportRequestParameters.searchDate = GetISODate(exportRequestParameters.searchDate);
    }
    if (exportRequestParameters.searchStartDate) {
      exportRequestParameters.searchStartDate = GetISODate(exportRequestParameters.searchStartDate);
    }
    if (exportRequestParameters.searchEndDate) {
      exportRequestParameters.searchEndDate = GetISODate(exportRequestParameters.searchEndDate);
    }

    let apiEndpoint = "";

    if (detailsGridApiEndpoint === "api/TotalSales/GetByBinDetails") {
      apiEndpoint = "api/TotalSales/PostTotalSalesByBinDetailsExcelExportRequestToAzureStorage";
    }
    else if (detailsGridApiEndpoint === "api/TotalSales/GetByBinAggregateDetails") {
      apiEndpoint = "api/TotalSales/PostTotalSalesByBinAggregateDetailsExcelExportRequestToAzureStorage";
    }

    let mutateParameter = {
      error: error,
      endpoint: apiEndpoint,
      directParameters: exportRequestParameters
    };

    mutateForServerSideExport(mutateParameter);
  }

  function setMainGridData(data) {
    let columns = [];

    if (data.length > 0) {
      let headerProps = {
        Payer: { title: "Payer Name", locked: true },
        BIN: { title: "BIN", locked: true },
        TotalSales: {
          title: "Total Sales",
          type: "money",
          showTotal: true,
          locked: true,
        },
      };

      let countColumnTitle = "Claim Count";

      if (binApiParams && binApiParams.dateType === "checkDate") {
        countColumnTitle = "Payment Count";
      }

      Object.keys(data[0]).map((key) => {

        if(key != "Pbid"){
                  return columns.push({
          title: headerProps[key]
            ? headerProps[key].title
            : key.includes("Count")
              ? countColumnTitle
              : key,
          type: headerProps[key]
            ? headerProps[key].type
            : key.includes("Count")
              ? "number"
              : "money",
          field: key,
          showTotal: headerProps[key] ? headerProps[key].showTotal : true,
          fixedLeft: headerProps[key] ? true : false,
          selectable: headerProps[key] || key.includes("Count") ? false : true,
          locked: headerProps[key] ? headerProps[key].locked : false,
        });
        }
      });

      setError({});
    } else {
      setError({ status: 201, Message: "No data found for given parameters" });
    }

    setMainGridColumns(columns);
  }
  return (
    <div className="feature-tab" id="total-sales-by-bin">
      <ErrorHandler
        error={error}
        onClose={() => {
          setError({});
        }}
      />
      <Accordion defaultExpanded label="Search & Filter">
        <Row>
          <Col className="higher-zindex-filters">
            <TotalSalesForm handleFormSubmit={handleFormSubmit} _uid="TSB" />
          </Col>
        </Row>
      </Accordion>
      <div ref={_container}>
        <Row>
          <Col className="tight-grid multi-line-filter">
            <SharedMainSelectableClientGrid
              pageSize={1000}
              ref={clientGridRef}
              apiGetEndpoint={"api/TotalSales/GetByBin"}
              apiParameters={binApiParams}
              aggregateColumnSettings={ConstructAggregateSetting(new Date(submittedFormData.fromDate), new Date(submittedFormData.toDate))}
              sendAggregatesParentCallback={sendMainAggregatesToParent}
              styleOverride={{ minHeight: "19em", maxHeight: "50em" }}
              onDataStateChangedCallback={() => {
                const allData = clientGridRef.current
                  ? clientGridRef.current.GetAllGridData()
                  : [];
                if (allData) {
                  setMainGridData(allData);
                }
              }}
              gridDataStateResetProcessingInitiated={
                gridDataStateResetProcessingInitiated
              }
              onGridDataStateResetCompleted={onGridDataStateResetCompleted}
              exportFileNamePrefix={"TotalSalesByBin"}>

              {mainGridColumns.length > 0
                ? mainGridColumns.map((singleColumn) =>
                  CreateGridColumn(singleColumn)
                )
                : null}
            </SharedMainSelectableClientGrid>
          </Col>
        </Row>
      </div>
      <DetailsModal
        title="Total Sales Detail"
        show={showModal}
        handleClose={() => {
          setShowModal(false)
        }}
      >
        <ErrorHandler
          error={detailError}
          onClose={() => {
            setDetailError({});
          }}
        />
        <ZIndexContext.Provider value={10003}>
          <Row>
            <Col className="tight-grid multi-line-filter">


              <SharedMainSelectableClientGrid
                ref={clientDetailGridRef}
                apiGetEndpoint={detailsGridApiEndpoint}
                apiParameters={detailsGridParameters}
                aggregateColumnSettings={detailGridAggregateColumnSettings}
                sendAggregatesParentCallback={sendDetailAggregatesToParent}
                styleOverride={{ minHeight: "19em", maxHeight: "50em" }}
                gridErrorHandlerCallback={detailsGridErrorHandlerCallback}
                useQueryOptions={{ refetchOnWindowFocus: false }}
                exportFileNamePrefix={"TotalSalesByBinDetails"}
                pageSize={50}
                >
                <GridColumn
                  key={"NCPDP"}
                  field={"NCPDP"}
                  title={"NCPDP"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  locked={true}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"Script"}
                  field={"Script"}
                  title={"Prescription"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  locked={true}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"DispensedDate"}
                  field={"DispensedDate"}
                  title={"Dispensed Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  locked={true}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"TransactionDate"}
                  field={"TransactionDate"}
                  title={"Transaction Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"Payer"}
                  field={"Payer"}
                  title={"Payer Name"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"BIN"}
                  field={"BIN"}
                  title={"BIN"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"PCN"}
                  field={"PCN"}
                  title={"PCN"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"AdjudicatedAmount"}
                  field={"AdjudicatedAmount"}
                  title={"Promise To Pay"}
                  width={DefaultColumnWidth()}
                  filter={"numeric"}
                  cells={{
                    data: MoneyCell,
                    footerCell: binApiParams.dateType !== "checkDate" ? DetailsGridAggregateMoneyCell : null,
                  }}
                  footerCell={binApiParams.dateType !== "checkDate" ? DetailsGridAggregateMoneyCell : null}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"PaidAmount"}
                  field={"PaidAmount"}
                  title={"Remittance Paid"}
                  width={DefaultColumnWidth()}
                  filter={"numeric"}
                  cells={{
                    data: MoneyCell,
                    footerCell: DetailsGridAggregateMoneyCell,
                  }}
                  footerCell={DetailsGridAggregateMoneyCell}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"CoPay"}
                  field={"CoPay"}
                  title={"Co-Pay"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  filter={"numeric"}
                  cells={{
                    data: MoneyCell,
                    footerCell: binApiParams.dateType !== "checkDate" ? DetailsGridAggregateMoneyCell : null,
                  }}
                  footerCell={binApiParams.dateType !== "checkDate" ? DetailsGridAggregateMoneyCell : null}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"NDC"}
                  field={"NDC"}
                  title={"NDC"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"Quantity"}
                  field={"Quantity"}
                  title={"Qty"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  filter={"numeric"}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"DaysSupply"}
                  field={"DaysSupply"}
                  title={"Days Supply"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  filter={"numeric"}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"AuthorizationID"}
                  field={"AuthorizationID"}
                  title={"Authorization ID"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"CardholderID"}
                  field={"CardholderID"}
                  title={"Cardholder ID"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"LastCheckNo"}
                  field={"LastCheckNo"}
                  title={"Last Received Check #"}
                  width={DefaultBooleanOrShortWidthColumnWidth()}
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"LastCheckDate"}
                  field={"LastCheckDate"}
                  title={"Last Received Check Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"LastCheckPaymentDate"}
                  field={"LastCheckPaymentDate"}
                  title={"Last Received Check Payment Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  headerCell={TooltipCell}
                />
                <GridColumn
                  key={"ArchiveDate"}
                  field={"ArchiveDate"}
                  title={"Payment/Check Archive Date"}
                  width={DefaultDateColumnWidth()}
                  filter={"date"}
                  format="{0:d}"
                  headerCell={TooltipCell}
                />
              </SharedMainSelectableClientGrid>
            </Col>
          </Row>
        </ZIndexContext.Provider>
      </DetailsModal>
    </div>
  );
}
